import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Search, Building2, Briefcase, MapPin, Globe, Mail,
  Check, Loader2, ArrowRight, Sparkles,
  AlertTriangle, Radar, Brain, Gauge
} from "lucide-react";
import { API_URL } from "../utils/api";
import { trackFunnel } from "../utils/trackFunnel";

const ANALYSIS_STEPS = [
  { text: "Aktiverar analysmotorn", icon: Gauge, duration: 1100 },
  { text: "Läser in branschdata", icon: Brain, duration: 1300 },
  { text: "Bedömer lokal konkurrens", icon: Radar, duration: 1400 },
  { text: "Sammanställer iakttagelser", icon: Sparkles, duration: 1500 },
];

// Between-step micro-transitions (motor processing a bit after each input)
const STEP_TRANSITIONS = {
  0: "Registrerar företaget",
  1: "Kopplar branschdata",
  2: "Hämtar lokal marknad",
  3: "Förbereder analys",
  3.5: "Läser in adressen",
  4: "Startar analysmotorn",
};

// SEVERITY_STYLES + POTENTIAL_CONFIG borttagna när resultatsidan ersattes med
// en mejl-pekande landningssida (se phase === "result"-blocket nedan). Detaljerna
// renderas nu i lead-mejlet istället.

export default function GratisAnalysPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [industry, setIndustry] = useState(searchParams.get("bransch") || searchParams.get("industry") || "");
  const [city, setCity] = useState(searchParams.get("stad") || searchParams.get("city") || "");
  const [websiteUrl, setWebsiteUrl] = useState(searchParams.get("url") || "");

  const [phase, setPhase] = useState("hero"); // hero, flow, transitioning, analyzing, result
  const [step, setStep] = useState(0);
  const [transitionText, setTransitionText] = useState("");
  const [company, setCompany] = useState("");
  const [hasWebsite, setHasWebsite] = useState(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const [completed, setCompleted] = useState([]);
  // Honeypot (hidden field - bots fill it in)
  const [website, setWebsite] = useState("");

  // Analyzing state
  const [analysisStep, setAnalysisStep] = useState(0); // -1 done, 0..n active
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  const sessionId = useRef(`analysis_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`).current;

  const addCompleted = (label, icon) => {
    setCompleted(prev => [...prev, { label, icon }]);
  };

  const TOTAL_STEPS = 5;

  // Show a short motor-transition between inputs: "Kopplar branschdata..." etc.
  const goToStep = async (next, fromStep) => {
    setError("");
    const key = fromStep !== undefined ? fromStep : step;
    const label = STEP_TRANSITIONS[key] || "Bearbetar";
    setTransitionText(label);
    setPhase("transitioning");
    // Short beat so it feels like the engine is doing something small
    await new Promise(r => setTimeout(r, 750));
    setStep(next);
    setPhase("flow");
  };

  const submitCompany = () => {
    if (!company.trim()) { setError("Ange företagsnamn"); return; }
    trackFunnel("step_company", "gratis-analys");
    addCompleted(company, Building2);
    goToStep(1, 0);
  };

  const submitIndustry = () => {
    if (!industry.trim()) { setError("Ange bransch"); return; }
    trackFunnel("step_industry", "gratis-analys");
    addCompleted(industry, Briefcase);
    goToStep(2, 1);
  };

  const submitCity = () => {
    if (!city.trim()) { setError("Ange stad"); return; }
    trackFunnel("step_city", "gratis-analys");
    addCompleted(city, MapPin);
    goToStep(3, 2);
  };

  const submitHasWebsite = (value) => {
    setHasWebsite(value);
    trackFunnel("step_website", "gratis-analys");
    addCompleted(value ? "Har hemsida" : "Ingen hemsida", Globe);
    if (value) goToStep(3.5, 3);
    else goToStep(4, 3);
  };

  const submitUrl = () => {
    trackFunnel("step_url", "gratis-analys");
    if (websiteUrl.trim()) {
      addCompleted(websiteUrl.replace(/^https?:\/\//, "").split("/")[0], Globe);
    }
    goToStep(4, 3.5);
  };

  // Animate analyzing progress
  useEffect(() => {
    if (phase !== "analyzing") return;

    let cancelled = false;
    const runSteps = async () => {
      const n = ANALYSIS_STEPS.length;
      for (let i = 0; i < n; i++) {
        if (cancelled) return;
        setAnalysisStep(i);
        const startPct = (i / n) * 100;
        const endPct = ((i + 1) / n) * 100;
        const duration = ANALYSIS_STEPS[i].duration;
        const ticks = 20;
        for (let t = 0; t <= ticks; t++) {
          if (cancelled) return;
          const pct = startPct + ((endPct - startPct) * t) / ticks;
          setProgress(pct);
          await new Promise(r => setTimeout(r, duration / ticks));
        }
      }
    };
    runSteps();
    return () => { cancelled = true; };
  }, [phase]);

  const submitEmail = async () => {
    if (!email.trim() || !email.includes("@")) { setError("Ange en giltig e-postadress"); return; }
    setError("");
    trackFunnel("step_email", "gratis-analys");

    setPhase("analyzing");
    setAnalysisStep(0);
    setProgress(0);

    // Minimum analyzing time feels premium (5s)
    const minDelay = new Promise(r => setTimeout(r, 5200));

    try {
      const apiCall = fetch(`${API_URL}/api/analysis/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name: company,
          industry,
          city,
          has_website: hasWebsite,
          website_url: websiteUrl || null,
          email,
          session_id: sessionId,
          website, // honeypot
        }),
      }).then(r => r.json());

      const [data] = await Promise.all([apiCall, minDelay]);

      if (data.success) {
        trackFunnel("analysis_complete", "gratis-analys", { potential: data.result.potential });
        setResult(data.result);
        setProgress(100);
        setAnalysisStep(ANALYSIS_STEPS.length); // mark all done
        // Small exhale before reveal
        await new Promise(r => setTimeout(r, 500));
        setPhase("result");
      } else {
        throw new Error(data.detail || "Analysen misslyckades");
      }
    } catch (e) {
      console.error("Analysis error:", e);
      setError("Något gick fel. Försök igen.");
      setPhase("flow");
      setStep(4);
    }
  };

  // potentialCfg removed — result page no longer renders the potential pill.

  return (
    <div className="min-h-screen bg-[#0a0f14] text-white font-['Inter'] antialiased selection:bg-white/20">
      <Helmet>
        <title>Gratis digital analys | Elido</title>
        <meta name="description" content="En kort, premium analys av hur ert företag står sig digitalt. Inga formulär – en tydlig bild på under en minut." />
      </Helmet>

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pulseRing {
          0% { transform: scale(0.85); opacity: 0.7; }
          100% { transform: scale(1.8); opacity: 0; }
        }
        @keyframes softGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.06); }
          50% { box-shadow: 0 0 40px 4px rgba(255,255,255,0.10); }
        }
        @keyframes slowSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes calmShimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @keyframes dotTravel {
          0% { transform: translateX(0); opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { transform: translateX(120px); opacity: 0; }
        }
        .anim-fade-up { animation: fadeUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .anim-fade-in { animation: fadeIn 0.7s ease-out both; }
        .anim-glow { animation: softGlow 3.2s ease-in-out infinite; }
        .ambient-grid {
          background-image:
            radial-gradient(circle at 20% 10%, rgba(255,255,255,0.035) 0%, transparent 45%),
            radial-gradient(circle at 85% 90%, rgba(255,255,255,0.03) 0%, transparent 40%);
        }
      `}</style>

      {/* ====================== HERO ====================== */}
      {phase === "hero" && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center relative ambient-grid">
          <div className="max-w-xl mx-auto anim-fade-up">
            <div className="inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-4 py-1.5 text-[11px] tracking-wide text-gray-400 mb-10 uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/80" />
              Kostnadsfri digital analys
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.05]">
              Hur mycket tappar ert<br/>företag digitalt?
            </h1>

            <p className="text-gray-400 text-base sm:text-lg mb-12 max-w-lg mx-auto leading-relaxed">
              Vi tittar på er bransch, er stad och er nuvarande närvaro. En lugn, ärlig bedömning – klar på under en minut.
            </p>

            <button
              onClick={() => { setPhase("flow"); setStep(0); trackFunnel("analysis_started", "gratis-analys"); }}
              className="group relative inline-flex items-center gap-3 bg-white text-[#0a0f14] px-10 py-5 rounded-2xl font-semibold text-lg hover:bg-gray-100 transition-all anim-glow"
              data-testid="start-analysis-btn"
            >
              <span className="relative flex items-center gap-3">
                <span className="relative">
                  <Search className="w-5 h-5" />
                </span>
                Starta analysen
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </span>
            </button>

            <p className="text-gray-600 text-xs mt-6">Ingen registrering. Inga säljsamtal i onödan.</p>
          </div>
        </div>
      )}

      {/* ====================== FLOW + TRANSITIONING + ANALYZING ====================== */}
      {(phase === "flow" || phase === "transitioning" || phase === "analyzing") && (
        <div className="min-h-screen flex flex-col px-6 pt-12 pb-12 relative ambient-grid">
          <div className="w-full max-w-xl mx-auto flex-1 flex flex-col">

            {/* Sticky header with subtle pulse-bar (no step counter) */}
            <div className="mb-8">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="relative">
                  {(phase === "analyzing" || phase === "transitioning") && (
                    <span className="absolute inset-0 rounded-full bg-white/15" style={{ animation: "pulseRing 2.2s ease-out infinite" }} />
                  )}
                  <div className="relative w-8 h-8 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center">
                    {phase === "analyzing" ? (
                      <Sparkles className="w-4 h-4 text-white" />
                    ) : phase === "transitioning" ? (
                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                    ) : (
                      <Radar className="w-4 h-4 text-gray-300" />
                    )}
                  </div>
                </div>
                <span className="text-xs tracking-wide text-gray-400 uppercase">
                  {phase === "analyzing"
                    ? "Analyserar"
                    : phase === "transitioning"
                    ? transitionText
                    : "Digital analys"}
                </span>
              </div>
              {/* Calm pulse-bar: animated but minimal */}
              <div className="h-[1.5px] bg-white/[0.04] rounded-full overflow-hidden relative">
                {phase === "analyzing" ? (
                  <div
                    className="h-full bg-white/40 transition-all duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                ) : (
                  <div className="absolute inset-0 h-full" style={{ animation: "calmShimmer 3.8s ease-in-out infinite" }}>
                    <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                  </div>
                )}
              </div>
            </div>

            {/* Completed badges */}
            {completed.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8 anim-fade-in">
                {completed.map((c, i) => (
                  <div key={i} className="flex items-center gap-1.5 bg-white/[0.04] border border-white/10 rounded-full px-3 py-1.5 text-xs text-gray-300">
                    <c.icon className="w-3 h-3 text-gray-500" />
                    <span>{c.label}</span>
                    <Check className="w-3 h-3 text-emerald-400/70" />
                  </div>
                ))}
              </div>
            )}

            {/* Step content */}
            <div className="flex-1 flex items-start pt-4">
              <div className="w-full">

                {/* Transitioning motor between steps */}
                {phase === "transitioning" && (
                  <div className="anim-fade-in pt-8 pb-8" key={`trans-${step}`}>
                    <div className="flex flex-col items-center">
                      <div className="relative w-12 h-12 mb-5">
                        <span className="absolute inset-0 rounded-full bg-white/10" style={{ animation: "pulseRing 1.6s ease-out infinite" }} />
                        <div className="relative w-full h-full rounded-full bg-white/[0.04] border border-white/15 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 text-white animate-spin" />
                        </div>
                      </div>
                      <p className="text-sm text-gray-300 tracking-tight">{transitionText}<span className="opacity-60">...</span></p>
                      {/* Traveling dots */}
                      <div className="relative w-32 h-4 mt-6 overflow-hidden">
                        <div className="absolute top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-white/60" style={{ animation: "dotTravel 1.4s ease-in-out infinite" }} />
                        <div className="absolute top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-white/40" style={{ animation: "dotTravel 1.4s ease-in-out infinite 0.25s" }} />
                        <div className="absolute top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-white/25" style={{ animation: "dotTravel 1.4s ease-in-out infinite 0.5s" }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 0: Company */}
                {phase === "flow" && step === 0 && (
                  <div className="anim-fade-up" key="s0">
                    <h2 className="text-2xl sm:text-3xl font-semibold mb-3 tracking-tight">Vad heter ert företag?</h2>
                    <p className="text-gray-500 text-sm mb-8">Vi använder det bara för att göra analysen personlig.</p>
                    <input
                      type="text"
                      value={company}
                      onChange={e => setCompany(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && submitCompany()}
                      placeholder="T.ex. Anderssons Måleri"
                      autoFocus
                      className="w-full px-0 py-4 bg-transparent border-0 border-b-2 border-white/15 text-white text-xl placeholder-gray-700 focus:outline-none focus:border-white/60 transition-colors"
                      data-testid="analysis-company-input"
                    />
                    {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
                    <button
                      onClick={submitCompany}
                      className="mt-10 inline-flex items-center gap-2 bg-white text-[#0a0f14] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
                      data-testid="analysis-company-submit"
                    >
                      Fortsätt <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Step 1: Industry */}
                {phase === "flow" && step === 1 && (
                  <div className="anim-fade-up" key="s1">
                    <h2 className="text-2xl sm:text-3xl font-semibold mb-3 tracking-tight">Vilken bransch är ni i?</h2>
                    <p className="text-gray-500 text-sm mb-8">Vi jämför mot andra inom samma område.</p>
                    <input
                      type="text"
                      value={industry}
                      onChange={e => setIndustry(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && submitIndustry()}
                      placeholder="T.ex. målare, VVS, frisör..."
                      autoFocus
                      className="w-full px-0 py-4 bg-transparent border-0 border-b-2 border-white/15 text-white text-xl placeholder-gray-700 focus:outline-none focus:border-white/60 transition-colors"
                      data-testid="analysis-industry-input"
                    />
                    <div className="flex flex-wrap gap-2 mt-5">
                      {["Målare", "Elektriker", "Snickare", "VVS", "Frisör", "Bygg"].map(ind => (
                        <button key={ind} onClick={() => setIndustry(ind)} type="button"
                          className={`px-3.5 py-1.5 rounded-full text-sm transition-all ${
                            industry === ind
                              ? "bg-white text-[#0a0f14]"
                              : "bg-white/[0.04] border border-white/10 text-gray-400 hover:bg-white/10 hover:text-white"
                          }`}>
                          {ind}
                        </button>
                      ))}
                    </div>
                    {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
                    <button
                      onClick={submitIndustry}
                      className="mt-10 inline-flex items-center gap-2 bg-white text-[#0a0f14] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
                      data-testid="analysis-industry-submit"
                    >
                      Fortsätt <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Step 2: City */}
                {phase === "flow" && step === 2 && (
                  <div className="anim-fade-up" key="s2">
                    <h2 className="text-2xl sm:text-3xl font-semibold mb-3 tracking-tight">Var finns ni?</h2>
                    <p className="text-gray-500 text-sm mb-8">Lokal marknad påverkar mycket av det vi tittar på.</p>
                    <input
                      type="text"
                      value={city}
                      onChange={e => setCity(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && submitCity()}
                      placeholder="T.ex. Stockholm, Malmö..."
                      autoFocus
                      className="w-full px-0 py-4 bg-transparent border-0 border-b-2 border-white/15 text-white text-xl placeholder-gray-700 focus:outline-none focus:border-white/60 transition-colors"
                      data-testid="analysis-city-input"
                    />
                    <div className="flex flex-wrap gap-2 mt-5">
                      {["Stockholm", "Göteborg", "Malmö", "Uppsala", "Örebro"].map(c => (
                        <button key={c} onClick={() => setCity(c)} type="button"
                          className={`px-3.5 py-1.5 rounded-full text-sm transition-all ${
                            city === c
                              ? "bg-white text-[#0a0f14]"
                              : "bg-white/[0.04] border border-white/10 text-gray-400 hover:bg-white/10 hover:text-white"
                          }`}>
                          {c}
                        </button>
                      ))}
                    </div>
                    {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
                    <button
                      onClick={submitCity}
                      className="mt-10 inline-flex items-center gap-2 bg-white text-[#0a0f14] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
                      data-testid="analysis-city-submit"
                    >
                      Fortsätt <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Step 3: Has website */}
                {phase === "flow" && step === 3 && (
                  <div className="anim-fade-up" key="s3">
                    <h2 className="text-2xl sm:text-3xl font-semibold mb-3 tracking-tight">Har ni en hemsida idag?</h2>
                    <p className="text-gray-500 text-sm mb-8">Påverkar hur vi bedömer möjligheterna framåt.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={() => submitHasWebsite(true)} type="button"
                        className="group py-6 bg-white/[0.03] border border-white/10 rounded-xl text-white font-medium hover:bg-white/[0.06] hover:border-white/25 transition-all text-left px-5"
                        data-testid="analysis-has-website-yes">
                        <Globe className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors mb-3" />
                        <span className="block text-base">Ja, vi har en hemsida</span>
                      </button>
                      <button onClick={() => submitHasWebsite(false)} type="button"
                        className="group py-6 bg-white/[0.03] border border-white/10 rounded-xl text-white font-medium hover:bg-white/[0.06] hover:border-white/25 transition-all text-left px-5"
                        data-testid="analysis-has-website-no">
                        <AlertTriangle className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors mb-3" />
                        <span className="block text-base">Nej, inte än</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3.5: Website URL */}
                {phase === "flow" && step === 3.5 && (
                  <div className="anim-fade-up" key="s35">
                    <h2 className="text-2xl sm:text-3xl font-semibold mb-3 tracking-tight">Vilken adress?</h2>
                    <p className="text-gray-500 text-sm mb-8">Valfritt – men gör analysen betydligt mer träffsäker.</p>
                    <input
                      type="url"
                      value={websiteUrl}
                      onChange={e => setWebsiteUrl(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && submitUrl()}
                      placeholder="www.ertföretag.se"
                      autoFocus
                      className="w-full px-0 py-4 bg-transparent border-0 border-b-2 border-white/15 text-white text-xl placeholder-gray-700 focus:outline-none focus:border-white/60 transition-colors"
                      data-testid="analysis-url-input"
                    />
                    <div className="flex flex-wrap gap-3 mt-10">
                      <button
                        onClick={submitUrl}
                        className="inline-flex items-center gap-2 bg-white text-[#0a0f14] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
                        data-testid="analysis-url-submit"
                      >
                        Fortsätt <ArrowRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { setWebsiteUrl(""); submitUrl(); }}
                        className="py-3 px-5 bg-transparent border border-white/10 rounded-xl text-gray-400 hover:text-white hover:border-white/25 transition-colors text-sm"
                      >
                        Hoppa över
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 4: Email */}
                {phase === "flow" && step === 4 && (
                  <div className="anim-fade-up" key="s4">
                    <h2 className="text-2xl sm:text-3xl font-semibold mb-3 tracking-tight">Vart skickar vi resultatet?</h2>
                    <p className="text-gray-500 text-sm mb-8">Analysen visas direkt. En kopia landar även i din inbox.</p>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && submitEmail()}
                      placeholder="din@email.se"
                      autoFocus
                      autoComplete="email"
                      className="w-full px-0 py-4 bg-transparent border-0 border-b-2 border-white/15 text-white text-xl placeholder-gray-700 focus:outline-none focus:border-white/60 transition-colors"
                      data-testid="analysis-email-input"
                    />
                    {/* Honeypot – hidden from humans, attractive to bots */}
                    <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                      <label>Website (lämna tomt)
                        <input
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          value={website}
                          onChange={e => setWebsite(e.target.value)}
                        />
                      </label>
                    </div>
                    {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
                    <button
                      onClick={submitEmail}
                      className="mt-10 inline-flex items-center gap-2 bg-white text-[#0a0f14] px-7 py-3.5 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
                      data-testid="analysis-email-submit"
                    >
                      <Sparkles className="w-4 h-4" />
                      Starta analysen
                    </button>
                    <p className="text-gray-600 text-xs mt-4">Vi skickar aldrig spam.</p>
                  </div>
                )}

                {/* Analyzing engine */}
                {phase === "analyzing" && (
                  <div className="anim-fade-in pt-4">
                    {/* Big central radar/pulse */}
                    <div className="flex items-center justify-center mb-12">
                      <div className="relative w-28 h-28">
                        <span className="absolute inset-0 rounded-full bg-white/10" style={{ animation: "pulseRing 2.4s ease-out infinite" }} />
                        <span className="absolute inset-0 rounded-full bg-white/10" style={{ animation: "pulseRing 2.4s ease-out infinite 0.8s" }} />
                        <div className="relative w-full h-full rounded-full bg-white/[0.04] border border-white/15 flex items-center justify-center anim-glow">
                          <div className="absolute inset-2 rounded-full border border-white/10" style={{ animation: "slowSpin 8s linear infinite" }} />
                          <div className="absolute inset-5 rounded-full border border-white/[0.07]" style={{ animation: "slowSpin 12s linear infinite reverse" }} />
                          <Sparkles className="w-7 h-7 text-white relative" />
                        </div>
                      </div>
                    </div>

                    <h2 className="text-2xl sm:text-3xl font-semibold text-center mb-2 tracking-tight">
                      Analyserar {company || "företaget"}
                    </h2>
                    <p className="text-gray-500 text-sm text-center mb-12">
                      {industry && city ? `${industry}, ${city}` : "Några sekunder kvar..."}
                    </p>

                    <div className="space-y-2.5 max-w-md mx-auto">
                      {ANALYSIS_STEPS.map((s, i) => {
                        const done = i < analysisStep;
                        const active = i === analysisStep;
                        const Icon = s.icon;
                        return (
                          <div
                            key={i}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-500 ${
                              done
                                ? "bg-white/[0.02] border-white/[0.06] opacity-60"
                                : active
                                ? "bg-white/[0.05] border-white/15"
                                : "bg-transparent border-white/[0.04] opacity-30"
                            }`}
                          >
                            <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                              done ? "bg-emerald-500/15 text-emerald-400" : active ? "bg-white/10 text-white" : "bg-white/5 text-gray-600"
                            }`}>
                              {done ? <Check className="w-3.5 h-3.5" /> : active ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
                            </div>
                            <span className={`text-sm ${done ? "text-gray-500" : active ? "text-white" : "text-gray-600"}`}>
                              {s.text}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====================== RESULT — Mejl-pekande landningssida ====================== */}
      {/*
        Tidigare visade vi hela analysen här. Nu håller vi sidan minimal och
        hänvisar kunden till mejlet. Detaljerna ligger i lead-mejlet — vinner i
        både upplevelse (clean reveal) och engagemang (kunden öppnar mejlet).
      */}
      {phase === "result" && result && (
        <div className="min-h-screen px-6 pt-16 pb-24 ambient-grid">
          <div className="max-w-lg mx-auto" data-testid="analysis-email-landing">

            {/* Meta label */}
            <div className="anim-fade-up flex items-center gap-2 text-xs tracking-wide text-gray-500 uppercase mb-8">
              <Sparkles className="w-3.5 h-3.5" />
              Analys klar
            </div>

            {/* Big mail icon + headline */}
            <div className="anim-fade-up mb-8" style={{ animationDelay: "0.05s" }}>
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.08] mb-6">
                <Mail className="w-6 h-6 text-white/80" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.15] mb-4">
                Vi har skickat analysen till din mejl
              </h1>
              <p className="text-gray-400 text-base leading-relaxed">
                Hela analysen för <span className="text-white">{company}</span> ligger nu i din inkorg.
              </p>
            </div>

            {/* Email row */}
            <div className="anim-fade-up flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/[0.08] px-4 py-3 mb-10" style={{ animationDelay: "0.15s" }}>
              <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span className="text-sm text-gray-300 truncate" data-testid="analysis-email-confirmation">{email}</span>
            </div>

            {/* Tiny helper note */}
            <p className="anim-fade-up text-xs text-gray-500 leading-relaxed mb-12" style={{ animationDelay: "0.2s" }}>
              Mejlet brukar dyka upp inom någon minut. Tipsar att kolla skräpposten om du inte ser det — och lägg gärna till <span className="text-gray-300">noreply@elido.nu</span> i din adressbok.
            </p>

            {/* Soft advisory CTA – not a hard sales pitch */}
            <div className="anim-fade-up rounded-2xl bg-white/[0.03] border border-white/[0.08] p-6 sm:p-8" style={{ animationDelay: "0.3s" }}>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                Vill du se ett exempel på hur en förbättrad version skulle kunna se ut för er?
              </p>
              <button
                onClick={() => {
                  // Handoff: alla fält från analysen så /gratis-demo kan skapa en riktad demo
                  // utan att be om samma info igen.
                  try {
                    const handoff = {
                      company_name: company,
                      industry,
                      city,
                      email,
                      has_website: hasWebsite,
                      website_url: websiteUrl ? websiteUrl.trim() : null,
                      session_id: sessionId,
                      from: "gratis-analys",
                      ts: Date.now(),
                    };
                    sessionStorage.setItem("analysis_handoff", JSON.stringify(handoff));
                  } catch {}
                  // Ensure we land at top on mobile after navigation
                  try { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); } catch { window.scrollTo(0, 0); }
                  navigate(
                    `/gratis-demo?company=${encodeURIComponent(company)}&industry=${encodeURIComponent(industry)}&city=${encodeURIComponent(city)}&from=analys`
                  );
                  // Double-tap after navigate in case browser restores scroll (mobile Safari quirk)
                  setTimeout(() => { try { window.scrollTo(0, 0); } catch {} }, 50);
                }}
                className="group inline-flex items-center gap-2 text-white border-b border-white/40 hover:border-white transition-colors pb-0.5 font-medium text-base"
                data-testid="analysis-to-demo-btn"
              >
                Visa ett möjligt exempel
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <p className="text-gray-600 text-xs mt-5">Inget sälj. Bara ett exempel baserat på er analys.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
